import React from 'react';
import { Controller, type FieldErrors } from 'react-hook-form';
import { Input } from '@planne-software/uni/Input';

import { useTranslate } from '@src/core/hooks';

import { maskCardNumber, maskHolderIdentity, ZIP_CODE_MASKS } from '../core/cardFieldMasks';
import type { CardFormValues } from '../core/cardPaymentFormSchema';
import type { CardFormVisibility } from '../core/resolveCardFormVisibility';
import type { CardFormControl } from '../types';
import { Grid } from '@planne-software/uni/Grid';

import { cardFieldSize } from '../styles';

type CardIdentityFieldsProps = {
  control: CardFormControl;
  errors: FieldErrors<CardFormValues>;
  visibility: CardFormVisibility;
  disabled: boolean;
  onZipCodeChange: (zipCode: string) => void;
  lookingUpAddress: boolean;
};

export const CardIdentityFields = (props: CardIdentityFieldsProps) => {
  const { control, errors, visibility, disabled, onZipCodeChange, lookingUpAddress } = props;
  const { i18n } = useTranslate();

  const zipCodeMask = visibility.zipCodeMask ? ZIP_CODE_MASKS[visibility.zipCodeMask] : undefined;

  return (
    <>
      <Grid size={cardFieldSize()} data-testid='card-form-card-number'>
        <Controller
          name='cardNumber'
          control={control}
          render={({ field: { ref, ...field } }) => (
            <Input
              {...field}
              id={field.name}
              inputRef={ref}
              label={i18n('checkout_payment_card_number')}
              required
              mask={maskCardNumber}
              inputMode='numeric'
              autoComplete='cc-number'
              disabled={disabled}
              disableClearable
              error={!!errors.cardNumber}
              helperText={errors.cardNumber?.message}
              size='medium'
            />
          )}
        />
      </Grid>

      <Grid size={cardFieldSize()} data-testid='card-form-holder'>
        <Controller
          name='holder'
          control={control}
          render={({ field: { ref, ...field } }) => (
            <Input
              {...field}
              id={field.name}
              inputRef={ref}
              label={i18n('checkout_payment_card_holder')}
              required
              autoComplete='cc-name'
              disabled={disabled}
              disableClearable
              error={!!errors.holder}
              helperText={errors.holder?.message}
              size='medium'
            />
          )}
        />
      </Grid>

      {visibility.showHolderIdentity ? (
        <Grid size={cardFieldSize()} data-testid='card-form-holder-identity'>
          <Controller
            name='holderIdentity'
            control={control}
            render={({ field: { ref, value, ...field } }) => (
              <Input
                {...field}
                value={value ?? ''}
                id={field.name}
                inputRef={ref}
                label={i18n('checkout_payment_card_holder_identity')}
                required
                mask={maskHolderIdentity}
                inputMode='numeric'
                disabled={disabled}
                disableClearable
                error={!!errors.holderIdentity}
                helperText={errors.holderIdentity?.message}
                size='medium'
              />
            )}
          />
        </Grid>
      ) : null}

      <Grid size={cardFieldSize()} data-testid='card-form-zip-code'>
        <Controller
          name='billingAddress.zipCode'
          control={control}
          render={({ field: { ref, value, onChange, ...field } }) => (
            <Input
              {...field}
              loading={lookingUpAddress}
              value={value ?? ''}
              onChange={(event) => {
                onChange(event);
                onZipCodeChange(event.target.value);
              }}
              id='billingAddressZipCode'
              inputRef={ref}
              label={i18n(visibility.zipCodeLabel)}
              required
              mask={zipCodeMask}
              autoComplete='postal-code'
              disabled={disabled}
              disableClearable
              error={!!errors.billingAddress?.zipCode}
              helperText={errors.billingAddress?.zipCode?.message}
              size='medium'
            />
          )}
        />
      </Grid>
    </>
  );
};

export default CardIdentityFields;
